import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type Permission = {
  permission_name: string;
  category: string;
  description: string;
};

// Define common permissions
export const PERMISSIONS = {
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_REPORTS: 'view_reports',
  VIEW_REVENUE_METRICS: 'view_revenue_metrics',
  VIEW_SETTINGS: 'view_settings',
  MANAGE_SETTINGS: 'manage_settings',
  MANAGE_USERS: 'manage_users',
  VIEW_INVENTORY: 'view_inventory',
  MANAGE_INVENTORY: 'manage_inventory',
  VIEW_ORDERS: 'view_orders',
  MANAGE_ORDERS: 'manage_orders',
  VIEW_CUSTOMERS: 'view_customers',
  MANAGE_CUSTOMERS: 'manage_customers'
} as const;

export const usePermissions = () => {
  const { user, signOut } = useAuth();
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'manager' | 'employee' | 'super_admin'>('employee');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setUserPermissions([]);
      setIsSuperAdmin(false);
      setUserRole('employee');
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        const { data: permissions, error: permError } = await supabase.rpc('get_user_permissions', {
          _user_id: user.id
        });

        if (permError) {
          console.error('Permission fetch error:', permError);
          throw permError;
        }
        
        setUserPermissions(permissions || []);

        // Fetch role and super admin status
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role, is_super_admin')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!roleError && roleData) {
          setUserRole(roleData.role);
          setIsSuperAdmin(roleData.is_super_admin || false);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setUserPermissions([]);
        setIsSuperAdmin(false);
        setUserRole('employee');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const hasPermission = useCallback((permission: string): boolean => {
    const hasAccess = isSuperAdmin || userPermissions.some(p => p.permission_name === permission);
    return hasAccess;
  }, [isSuperAdmin, userPermissions]);

  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    if (isSuperAdmin) return true;
    return permissions.some(permission => hasPermission(permission));
  }, [isSuperAdmin, hasPermission]);

  const hasAllPermissions = useCallback((permissions: string[]): boolean => {
    if (isSuperAdmin) return true;
    return permissions.every(permission => hasPermission(permission));
  }, [isSuperAdmin, hasPermission]);

  const canAccessPage = useCallback((requiredPermissions: string[]): boolean => {
    return hasAnyPermission(requiredPermissions);
  }, [hasAnyPermission]);

  const getPermissionsByCategory = useCallback((category: string): Permission[] => {
    return userPermissions.filter(p => p.category === category);
  }, [userPermissions]);

  return {
    userPermissions,
    isSuperAdmin,
    userRole,
    loading,
    user,
    signOut,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessPage,
    getPermissionsByCategory
  };
};